import type * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const stackVariants = cva("flex", {
  variants: {
    direction: {
      column: "flex-col",
      row: "flex-row",
      "column-reverse": "flex-col-reverse",
      "row-reverse": "flex-row-reverse",
    },
    gap: {
      0: "gap-0",
      1: "gap-1",
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      6: "gap-6",
      8: "gap-8",
      12: "gap-12",
      16: "gap-16",
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
      baseline: "items-baseline",
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly",
    },
    wrap: {
      nowrap: "flex-nowrap",
      wrap: "flex-wrap",
      "wrap-reverse": "flex-wrap-reverse",
    },
    inline: {
      true: "inline-flex",
      false: "",
    },
  },
  defaultVariants: {
    direction: "column",
    gap: 2,
    inline: false,
  },
});

export interface StackProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stackVariants> {
  asChild?: boolean;
}

const Stack = ({
  ref,
  className,
  direction,
  gap,
  align,
  justify,
  wrap,
  inline,
  asChild = false,
  ...props
}: StackProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      data-slot="stack"
      ref={ref}
      className={cn(
        stackVariants({ direction, gap, align, justify, wrap, inline }),
        className,
      )}
      {...props}
    />
  );
};
Stack.displayName = "Stack";

export { Stack, stackVariants };
